<template>
  <div class="explore">
    <div class="explore__search-container" v-if="firstVisit && !hasActiveParameters">
      <div class="explore__search-content">
        <SmallLogo class="explore__smiley" />
        <div class="page-contact-directory__welcome-text">
          <div
            class="page-contact-directory__welcome-message"
            v-html="$t('page.explore.search-all', { exploreCount: exploreCount.toLocaleString('en-US') })"
          ></div>
          <div class="page-contact-directory__welcome-message--subtitle">
            {{ $t('page.explore.search-all-subtitle') }}
          </div>
        </div>
        <ExploreBar @search-clicked="searchClicked" />
      </div>
    </div>
    <div v-else>
      <ExploreHeader :totalCount="exploreCount" />
      <div class="explore__main">
        <div class="explore__filters" ref="topAnchor">
          <!-- Phones: the search and the filters on one line, as on the other lists. -->
          <PhoneFilterBar v-if="isPhone" :active-count="activeFilterCount" @clear-all="onClearAllFilters">
            <template #search>
              <ExploreBar />
            </template>
            <template #filters>
              <ExploreFilters :searchFilters="searchFilters" v-on="filterListeners" />
            </template>
          </PhoneFilterBar>
          <ExploreFilters v-else :searchFilters="searchFilters" v-on="filterListeners" />
        </div>
        <TendersDataTable
          :mode="mode"
          :pending="pending"
          :tenders="tenders"
          @rowClicked="openTenderDetails"
          @open-opportunity="openOpportunityDetails"
          @pageChanged="changePage"
          @refresh-tenders="onRefreshRequested"
          @load-more="loadMore"
          @export-tenders="exportTenders"
        />
        <v-overlay :value="pending" absolute opacity="0.5" color="#ffffff" z-index="1"></v-overlay>
      </div>
      <v-overlay :value="pending" absolute opacity="0.5" color="#ffffff" z-index="1"></v-overlay>

      <FilterAdvancedDialog
        v-if="advancedSearchFilters"
        :searchFilters="searchFilters"
        @set-search-filters="updateSearchFilters"
        @close-dialog="closeAdvancedFilters"
        explore
      />
    </div>
  </div>
</template>

<script>
import ExploreBar from '@/components/page-explore/ExploreBar';
import ExploreHeader from '@/components/page-explore/ExploreHeader';
import FilterAdvancedDialog from '@/components/page-tenders/FilterAdvancedDialog';
import SmallLogo from '@/assets/images/aloha-logomark.svg';
import ExploreFilters from '@/components/page-explore/ExploreFilters';
import PhoneFilterBar from '@/components/filters/PhoneFilterBar.vue';
import TendersDataTable from '@/components/page-tenders/TendersDataTable.vue';

import { bus } from '@/main';
import { datesArrayToObject } from '@/util/date-util';
import { mapActions, mapGetters } from 'vuex';
import { Modes } from '@/models/modes';
import DebounceMixin from '@/mixins/debounce-mixin';
import { highlightPhoneRow } from '@/util/highlight-phone-row';
import { exploreFilterCount } from '@/util/explore-filters';
import { viewport } from '@/util/viewport';

export default {
  name: 'PageExplore',
  mixins: [DebounceMixin],
  components: {
    ExploreBar,
    ExploreHeader,
    FilterAdvancedDialog,
    SmallLogo,
    ExploreFilters,
    PhoneFilterBar,
    TendersDataTable,
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('modes', ['mode']),
    ...mapGetters('tenant', ['tenant']),
    ...mapGetters('tenders', ['pending', 'tenders', 'exploreCount', 'page', 'previousPage', 'nextPage']),
    ...mapGetters('exploreSearchFilters', ['searchFilters', 'keywords', 'hasActiveFilters']),
    isPhone() {
      return viewport.size === 'phone';
    },
    // The filters, in the page's row or a phone's filter bar.
    filterListeners() {
      return {
        'advanced-filters': this.openAdvancedFilters,
        'advanced-clear': this.onAdvancedClear,
        'platform-change': this.onPlatformsChange,
        'seao-filters-change': this.onSEAOFiltersChange,
        'status-change': this.onStatusesChange,
        'read-filter-change': this.onReadFiltersChange,
        'share-filter-change': this.onShareFiltersChange,
        'follow-filter-change': this.onFollowFiltersChange,
        'closing-before-change': this.onClosingBeforeChange,
        'publication-dates-change': this.onPublicationDatesChange,
        'assignees-filter-change': this.onAssigneesFilterChange,
      };
    },
    activeFilterCount() {
      return exploreFilterCount(this.searchFilters);
    },
    hasActiveParameters() {
      const hasKeywords = !!this.keywords && !!this.keywords.length;

      return hasKeywords || this.hasActiveFilters;
    },
  },
  data() {
    return {
      firstVisit: true,
      lastViewedTenderId: null,
      advancedSearchFilters: false,
    };
  },
  watch: {
    mode() {
      if (this.mode === Modes.EXPLORE.name) {
        this.onSearchRequested(1);
      }
    },
  },
  methods: {
    datesArrayToObject,
    ...mapActions('tenders', ['refreshTenders', 'getExploreCount', 'getTendersByMode']),
    ...mapActions('exportTenders', ['getExportTenders']),
    ...mapActions('exploreSearchFilters', [
      'resetExploreSearchFilters',
      'setAdvancedSearchFilters',
      'setPlatforms',
      'setAdministrativeRegions',
      'setClassifications',
      'setStatuses',
      'setReadFilters',
      'setShareFilters',
      'setFollowFilters',
      'setClosingBefore',
      'setPublicationDates',
      'setAssigneesFilters',
      'setKeywords',
    ]),
    onSearchRequested(page = 1) {
      if (!this.mode) return;

      const mode = Modes[`${this.mode}`];
      this.getTendersByMode({ searchFilters: this.searchFilters, mode, page });
    },
    exportTenders() {
      const mode = Modes[`${this.mode}`];
      this.getExportTenders({ mode, searchFilters: this.searchFilters }).catch((error) => {
        this.$toast.error(error);
      });
    },
    changePage(page) {
      // Cancel a pending filter-change search (it would reset to page 1) so paginating wins and the
      // list doesn't snap back after the 700ms debounce fires.
      this.debounce(() => {}, 0, true);
      this.onRefreshRequested(page);
      this.$refs.topAnchor.scrollIntoView({ block: 'end', behavior: 'smooth', inline: 'start' });
    },
    onPlatformsChange(platforms) {
      this.setPlatforms(platforms);
      this.debouncedSearch();
    },
    onSEAOFiltersChange(filters) {
      this.setAdministrativeRegions({ platform: 'seao_v2', regions: filters.regions });
      this.setClassifications({ classifications: filters.classifications });
      this.debouncedSearch();
    },
    onStatusesChange(statuses) {
      this.setStatuses(statuses);
      this.debouncedSearch();
    },
    onClosingBeforeChange(closingBefore) {
      this.setClosingBefore(closingBefore);
      this.debouncedSearch();
    },
    onPublicationDatesChange(publicationDates) {
      this.setPublicationDates(datesArrayToObject(publicationDates));
      this.debouncedSearch();
    },
    onReadFiltersChange(readFilters) {
      this.setReadFilters(readFilters);
      this.debouncedSearch();
    },
    onFollowFiltersChange(followFilters) {
      this.setFollowFilters(followFilters);
      this.debouncedSearch();
    },
    onShareFiltersChange(shareFilters) {
      this.setShareFilters(shareFilters);
      this.debouncedSearch();
    },
    onAssigneesFilterChange(assignees) {
      this.setAssigneesFilters(assignees);
      this.debouncedSearch();
    },
    // Debounce so a multi-select session fires one search once the user pauses, not one per
    // click (700ms). Pagination stays immediate via onRefreshRequested.
    debouncedSearch() {
      this.debounce(() => this.onRefreshRequested(1), 700);
    },
    onRefreshRequested(page = 1) {
      if (!this.mode || this.mode !== Modes.EXPLORE.name) return;
      if (this.firstVisit && !this.hasActiveParameters) return;

      this.refreshTenders({ searchFilters: this.searchFilters, page }).then(() => {
        if (this.tenders.length < 1 && this.previousPage) {
          this.refreshTenders({ searchFilters: this.searchFilters, page: this.previousPage });
        }
      });
    },
    // The phone list's next page. A failure shows in the list, with a retry.
    loadMore() {
      if (this.nextPage === null) return;
      this.refreshTenders({ searchFilters: this.searchFilters, page: Number(this.nextPage), append: true }).catch(
        () => {},
      );
    },
    // Every filter, but not the search.
    onClearAllFilters() {
      const { keywords } = this;
      this.resetExploreSearchFilters();
      this.setKeywords({ keywords });
      this.onRefreshRequested(1);
    },
    onAdvancedClear() {
      this.setAdvancedSearchFilters({ keywordIds: [], matchContexts: [], procurementCategories: [], zones: {} });
      this.onRefreshRequested(1);
    },
    openAdvancedFilters() {
      this.advancedSearchFilters = true;
    },
    closeAdvancedFilters() {
      this.advancedSearchFilters = false;
    },
    updateSearchFilters(tempSearchFilters) {
      this.setAdvancedSearchFilters(tempSearchFilters);
      this.onRefreshRequested();
    },
    openTenderDetails(tenderId) {
      this.$track.tenderView();
      this.$router.push({ name: 'tender-details', params: { id: tenderId } });
    },
    openOpportunityDetails(opportunity) {
      if (!opportunity) return;
      this.$router.push({
        name: 'opportunity-details',
        params: { opportunityId: opportunity.id },
      });
    },
    highlightTender(tenderId) {
      const elementId = `item-${tenderId}`;
      this.$nextTick(() => this.goToElement(elementId));
    },
    goToElement(elementId) {
      const element = document.getElementById(elementId);

      if (element) {
        const tr = element.closest('tr');
        element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        if (tr) {
          tr.className = 'highlight';
          setTimeout(() => {
            tr.className = '';
          }, 1200);
        } else {
          highlightPhoneRow(element);
        }
      }
    },
    searchClicked() {
      this.firstVisit = false;
    },
  },
  created() {
    this.resetExploreSearchFilters();
    this.getExploreCount(); // First time, we call it directly

    bus.$on('last-viewed-tender-id', (id) => {
      this.lastViewedTenderId = id;
    });

    bus.$on('refresh-explore-page', () => {
      this.onRefreshRequested(this.page);
    });

    this.$track.exploreView();
  },
  activated() {
    if (this.lastViewedTenderId) {
      this.$nextTick(() => {
        this.highlightTender(this.lastViewedTenderId);
        this.lastViewedTenderId = null;
      });
    }
  },
  beforeRouteLeave(to, from, next) {
    // If the user wants to see a tender or opportunity details, we don't reset the search filters
    if (to.name === 'tender-details' || to.name === 'opportunity-details') {
      next();
      return;
    }

    this.resetExploreSearchFilters();
    this.firstVisit = true;

    next();
  },
};
</script>

<style lang="scss">
.explore {
  &__error {
    display: flex;
    justify-content: center;
    height: 80vh;
  }

  &__search-container {
    background: $color-white;
    display: flex;
    height: calc(var(--viewport-height) - 56px);
  }

  &__search-content {
    min-width: 600px;
    display: block;
    margin: auto;
  }

  &__smiley {
    width: 140px;
    height: 140px;
    display: block;
    margin: 0 auto 64px;
  }

  &__main {
    display: flex;
    flex: auto;
    flex-direction: column;
    padding: 24px 32px 32px 32px;

    @include screen-below('tablet') {
      padding-right: $phone-page-gutter;
      padding-left: $phone-page-gutter;
    }
  }

  // Full-bleed white surface holding the filter chips, butted under the sticky header
  // (mirrors the tenders inbox views panel).
  &__filters {
    background: $color-white;
    box-shadow: $shadow-dropdown;
    margin: -24px -32px 16px;
    padding: 12px 32px 14px;
    position: relative;
    z-index: 2;

    @include phone-filter-panel;
  }

  // As the contact directory's welcome: off the edges of a small screen.
  @media screen and (max-width: 784px) {
    &__search-content {
      width: calc(100% - 56px);
      min-width: 300px;
    }
  }
}
</style>
